'use client';

import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useSearch, useSearchMeta } from '../hooks/queries/useSearch';
import { useMeProjects } from '../hooks/queries/useMeProjects';
import type { SearchEntityType, SearchProvider, SearchResultItem } from '../lib/api';
import { cn } from '../lib/cn';
import { resolveAppHref } from '../lib/portalRoutes';
import {
  badgeSizes,
  coActionStatusBadge,
  coActionStatusLabel,
  completionReportStatusBadge,
  completionReportStatusLabel,
  planStatusBadge,
  planStatusLabel,
  postMortemStatusBadge,
  postMortemStatusLabel,
  codeReviewStatusBadge,
  codeReviewStatusLabel,
  type StatusBadgeEntityType,
} from '../lib/badgeColors';
import { EntityStatusBadge } from './EntityStatusBadge';
import { BitbucketIcon, GitHubIcon, GitLabIcon, LinearIcon, SentryIcon } from './icons/ProviderIcons';
import { BaseModal } from './BaseModal';
import { LoadMoreButton } from './LoadMoreButton';
import { Select, type SelectOption } from './Select';
import { AgentTeamsLottieLoader } from './loading/AgentTeamsLottieLoader';
import { Spinner } from './loading/Spinner';

const SEARCH_LIMIT_STEP = 20;
const SEARCH_LIMIT_MAX = 100;
const SEARCH_CENTER_STATE_CLASS_NAME = 'flex h-full min-h-[18rem] items-center justify-center pb-16';
const SEARCH_CENTER_LOGO_CLASS_NAME = 'h-14 w-14';

type SearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  projectId: string;
  currentContextQuery: string;
  hideProjectSelector?: boolean;
  onInternalResultSelect?: (input: { item: SearchResultItem; projectId: string }) => boolean;
};

const entityTypeRoute: Record<SearchEntityType, string> = {
  PLAN: '/plans/detail',
  CO_ACTION: '/co-actions/detail',
  COMPLETION_REPORT: '/completion-reports/detail',
  POST_MORTEM: '/post-mortems/detail',
  CONVENTION: '/conventions/detail',
  SKILL: '/skills/detail',
  COMMENT: '/plans/detail',
  CODE_REVIEW: '/code-reviews/detail',
  DOCUMENT: '/documents/detail',
  GITHUB_ISSUE: '',
  GITHUB_PR: '',
  GITLAB_ISSUE: '',
  GITLAB_MERGE_REQUEST: '',
  BITBUCKET_ISSUE: '',
  BITBUCKET_PR: '',
  LINEAR_ISSUE: '',
  SENTRY_ISSUE: '',
};

const entityTypeIcon: Record<SearchEntityType, string> = {
  PLAN: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  CO_ACTION:
    'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  COMPLETION_REPORT:
    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  POST_MORTEM:
    'M9 17v-2m3 2v-4m3 4V7m2 12H7a2 2 0 01-2-2V5a2 2 0 012-2h7.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V17a2 2 0 01-2 2z',
  CONVENTION:
    'M12 6.253v13m0-13C10.832 5.483 9.246 5 7.5 5 4.462 5 2 6.567 2 8.5v10C2 16.567 4.462 15 7.5 15c1.746 0 3.332.483 4.5 1.253m0-10C13.168 5.483 14.754 5 16.5 5c3.038 0 5.5 1.567 5.5 3.5v10c0-1.933-2.462-3.5-5.5-3.5-1.746 0-3.332.483-4.5 1.253',
  // 사이드바 SkillsIcon(lucide puzzle)과 같은 모양. 번개는 러너의 표식이라 여기서 쓰면 안 된다.
  SKILL:
    'M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z',
  COMMENT:
    'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  CODE_REVIEW:
    'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  DOCUMENT:
    'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
  GITHUB_ISSUE:
    'M9 12h6m-6 4h3m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  GITHUB_PR: 'M7 7h10M7 12h4m-4 5h10M5 3v18m14-4l2 2-2 2M19 15l-2 2 2 2',
  GITLAB_ISSUE: 'M12 7v5m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.65 18h16.7a1 1 0 00.86-1.5l-7.5-13a1 1 0 00-1.72 0z',
  GITLAB_MERGE_REQUEST:
    'M7 5a2 2 0 114 0v8a2 2 0 11-2 2m0-10H17m0 0l-3-3m3 3l-3 3M7 19a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4z',
  BITBUCKET_ISSUE:
    'M9 12h6m-6 4h3m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  BITBUCKET_PR: 'M7 7h10M7 12h4m-4 5h10M5 3v18m14-4l2 2-2 2M19 15l-2 2 2 2',
  LINEAR_ISSUE:
    'M9 12h6m-6 4h3m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  SENTRY_ISSUE:
    'M9 12h6m-6 4h3m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
};

const entityTypeBadge: Record<SearchEntityType, { color: string }> = {
  PLAN: { color: 'text-primary bg-primary/10' },
  CO_ACTION: { color: 'text-secondary bg-secondary/10' },
  COMPLETION_REPORT: { color: 'text-accent bg-accent/10' },
  POST_MORTEM: { color: 'text-warning bg-warning/10' },
  CONVENTION: { color: 'text-info bg-info/10' },
  SKILL: { color: 'text-success bg-success/10' },
  COMMENT: { color: 'text-info bg-info/10' },
  CODE_REVIEW: { color: 'text-success bg-success/10' },
  DOCUMENT: { color: 'text-neutral bg-neutral/10' },
  GITHUB_ISSUE: { color: 'text-success bg-success/10' },
  GITHUB_PR: { color: 'text-neutral bg-neutral/10' },
  GITLAB_ISSUE: { color: 'text-warning bg-warning/10' },
  GITLAB_MERGE_REQUEST: { color: 'text-error bg-error/10' },
  BITBUCKET_ISSUE: { color: 'text-info bg-info/10' },
  BITBUCKET_PR: { color: 'text-primary bg-primary/10' },
  LINEAR_ISSUE: { color: 'text-primary bg-primary/10' },
  SENTRY_ISSUE: { color: 'text-error bg-error/10' },
};

type EntityTypeEntry = { type: SearchEntityType; labelKey: string; provider?: SearchProvider };

const allEntityTypes: EntityTypeEntry[] = [
  { type: 'PLAN', labelKey: 'searchTypePlan' },
  { type: 'CO_ACTION', labelKey: 'searchTypeCoAction' },
  { type: 'COMPLETION_REPORT', labelKey: 'searchTypeCompletionReport' },
  { type: 'POST_MORTEM', labelKey: 'searchTypePostMortem' },
  { type: 'CONVENTION', labelKey: 'searchTypeConvention' },
  { type: 'SKILL', labelKey: 'searchTypeSkill' },
  { type: 'COMMENT', labelKey: 'searchTypeComment' },
  { type: 'CODE_REVIEW', labelKey: 'searchTypeCodeReview' },
  { type: 'DOCUMENT', labelKey: 'searchTypeDocument' },
  { type: 'GITHUB_ISSUE', labelKey: 'searchTypeGithubIssue', provider: 'github' },
  { type: 'GITHUB_PR', labelKey: 'searchTypeGithubPr', provider: 'github' },
  { type: 'GITLAB_ISSUE', labelKey: 'searchTypeGitlabIssue', provider: 'gitlab' },
  { type: 'GITLAB_MERGE_REQUEST', labelKey: 'searchTypeGitlabMergeRequest', provider: 'gitlab' },
  { type: 'BITBUCKET_ISSUE', labelKey: 'searchTypeBitbucketIssue', provider: 'bitbucket' },
  { type: 'BITBUCKET_PR', labelKey: 'searchTypeBitbucketPr', provider: 'bitbucket' },
  { type: 'LINEAR_ISSUE', labelKey: 'searchTypeLinearIssue', provider: 'linear' },
  { type: 'SENTRY_ISSUE', labelKey: 'searchTypeSentryIssue', provider: 'sentry' },
];

const formatRelativeDate = (dateStr: string): string => {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const sanitizeSnippet = (snippet: string): string => {
  // Strip raw Tiptap JSON fragments from snippets (e.g. "type\":\"text\",\"marks\":[...")
  if (snippet.includes('"type"') && snippet.includes('"content"')) {
    try {
      // Try to extract text values from partial JSON
      const textMatches = snippet.match(/"text"\s*:\s*"([^"]+)"/g);
      if (textMatches && textMatches.length > 0) {
        const texts = textMatches
          .map((m) => m.replace(/^"text"\s*:\s*"/, '').replace(/"$/, ''))
          .filter(
            (t) =>
              t.length > 1 &&
              ![
                'text',
                'bold',
                'code',
                'paragraph',
                'heading',
                'listItem',
                'bulletList',
                'orderedList',
                'blockquote',
                'codeBlock',
              ].includes(t),
          );
        if (texts.length > 0) return texts.join(' ');
      }
    } catch {
      /* fallback to original */
    }
  }
  return snippet.replace(/\s+/g, ' ').trim();
};

// 진행 글리프(EntityStatusBadge, dot)로 표기하는 상태 보유 엔티티.
// COMMENT/외부 이슈는 레지스트리 밖이라 getStatusBadge의 ad-hoc 톤(pill)을 그대로 쓴다.
const ICON_STATUS_ENTITY_TYPES = new Set<SearchEntityType>([
  'PLAN',
  'COMPLETION_REPORT',
  'POST_MORTEM',
  'CO_ACTION',
  'CODE_REVIEW',
]);

const getStatusBadge = (entityType: SearchEntityType, status: string | null) => {
  if (!status) return null;

  switch (entityType) {
    case 'PLAN':
      if (status in planStatusBadge) {
        const key = status as keyof typeof planStatusBadge;
        return { tone: planStatusBadge[key], label: planStatusLabel[key] };
      }
      return null;
    case 'CO_ACTION':
      if (status in coActionStatusBadge) {
        const key = status as keyof typeof coActionStatusBadge;
        return { tone: coActionStatusBadge[key], label: coActionStatusLabel[key] };
      }
      return null;
    case 'COMPLETION_REPORT':
      if (status in completionReportStatusBadge) {
        const key = status as keyof typeof completionReportStatusBadge;
        return { tone: completionReportStatusBadge[key], label: completionReportStatusLabel[key] };
      }
      return null;
    case 'POST_MORTEM':
      if (status in postMortemStatusBadge) {
        const key = status as keyof typeof postMortemStatusBadge;
        return { tone: postMortemStatusBadge[key], label: postMortemStatusLabel[key] };
      }
      return null;
    case 'CONVENTION':
    case 'SKILL':
      // 상태 축이 없는 엔티티다(둘 다 status 컬럼이 없다).
      return null;
    case 'COMMENT': {
      const lower = status.toLowerCase();
      if (lower === 'risk') return { tone: 'bg-error/20 text-error', label: status };
      if (lower === 'modification') return { tone: 'bg-warning/20 text-warning', label: status };
      if (lower === 'general')
        return { tone: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]', label: status };
      return { tone: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]', label: status };
    }
    case 'CODE_REVIEW':
      if (status in codeReviewStatusBadge) {
        const key = status as keyof typeof codeReviewStatusBadge;
        return { tone: codeReviewStatusBadge[key], label: codeReviewStatusLabel[key] };
      }
      return null;
    case 'DOCUMENT':
      return null;
    case 'GITHUB_ISSUE':
    case 'GITHUB_PR':
    case 'GITLAB_ISSUE':
    case 'GITLAB_MERGE_REQUEST':
    case 'BITBUCKET_ISSUE':
    case 'BITBUCKET_PR':
    case 'LINEAR_ISSUE': {
      const lower = status.toLowerCase();
      if (lower === 'open' || lower === 'opened') return { tone: 'bg-success/20 text-success', label: status };
      if (lower === 'closed') return { tone: 'bg-error/20 text-error', label: status };
      if (lower === 'merged') return { tone: 'bg-primary/20 text-primary', label: status };
      return { tone: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]', label: status };
    }
    case 'SENTRY_ISSUE': {
      const lower = status.toLowerCase();
      if (lower === 'unresolved') return { tone: 'bg-error/20 text-error', label: status };
      if (lower === 'resolved') return { tone: 'bg-success/20 text-success', label: status };
      return { tone: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]', label: status };
    }
    default:
      return null;
  }
};

const entityTypeBadgeClassByType: Partial<Record<SearchEntityType, string>> = {
  PLAN: 'bg-primary/10 text-primary',
  CO_ACTION: 'bg-secondary/10 text-secondary',
  COMPLETION_REPORT: 'bg-accent/10 text-accent',
  POST_MORTEM: 'bg-warning/10 text-warning',
  CONVENTION: 'bg-info/10 text-info',
  SKILL: 'bg-success/10 text-success',
  COMMENT: 'bg-info/10 text-info',
  CODE_REVIEW: 'bg-success/10 text-success',
  DOCUMENT: 'bg-neutral/10 text-neutral',
  GITHUB_ISSUE: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  GITHUB_PR: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  GITLAB_ISSUE: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  GITLAB_MERGE_REQUEST: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  BITBUCKET_ISSUE: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  BITBUCKET_PR: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  LINEAR_ISSUE: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
  SENTRY_ISSUE: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
};

const entityTypeLabelKeyByType: Partial<Record<SearchEntityType, string>> = {
  PLAN: 'searchTypePlan',
  CO_ACTION: 'searchTypeCoAction',
  COMPLETION_REPORT: 'searchTypeCompletionReport',
  POST_MORTEM: 'searchTypePostMortem',
  CONVENTION: 'searchTypeConvention',
  SKILL: 'searchTypeSkill',
  COMMENT: 'searchTypeComment',
  CODE_REVIEW: 'searchTypeCodeReview',
  DOCUMENT: 'searchTypeDocument',
  GITHUB_ISSUE: 'searchTypeGithubIssue',
  GITHUB_PR: 'searchTypeGithubPr',
  GITLAB_ISSUE: 'searchTypeGitlabIssue',
  GITLAB_MERGE_REQUEST: 'searchTypeGitlabMergeRequest',
  BITBUCKET_ISSUE: 'searchTypeBitbucketIssue',
  BITBUCKET_PR: 'searchTypeBitbucketPr',
  LINEAR_ISSUE: 'searchTypeLinearIssue',
  SENTRY_ISSUE: 'searchTypeSentryIssue',
};

const getEntityTypeBadgeLabel = (
  entityType: SearchEntityType,
  t: (key: string) => string,
): { label: string; className: string } | null => {
  const className = entityTypeBadgeClassByType[entityType];
  const labelKey = entityTypeLabelKeyByType[entityType];
  if (!className || !labelKey) return null;
  return { label: t(labelKey), className };
};

const shortcutKeyClassName =
  'inline-flex min-h-0 items-center rounded-md border border-base-content/10 bg-base-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-[var(--color-fg-subtle)]';

const shortcutKeyInputClassName =
  'inline-flex min-h-0 items-center rounded-md border border-base-content/10 bg-base-200 px-2.5 py-1 text-xs font-medium leading-none text-[var(--color-fg-subtle)]';

export const SearchModal = ({
  isOpen,
  onClose,
  token,
  projectId,
  currentContextQuery,
  hideProjectSelector = false,
  onInternalResultSelect,
}: SearchModalProps) => {
  const t = useTranslations('components.appLayout');
  const tCommon = useTranslations('common');
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<SearchEntityType[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [navigatingId, setNavigatingId] = useState<string | null>(null);
  const [currentLimit, setCurrentLimit] = useState(SEARCH_LIMIT_STEP);
  const [searchProjectId, setSearchProjectId] = useState(projectId);
  const [, startTransition] = useTransition();

  const { data: meProjects = [] } = useMeProjects(token);
  const projectOptions = useMemo<SelectOption[]>(
    () =>
      meProjects.flatMap((bundle) =>
        bundle.projects.map((project) => ({
          value: project.id,
          label: `${bundle.team.name} / ${project.name}`,
          // 스코프 트리거는 공간을 아끼려 이니셜 아바타만 노출(풀네임은 드롭다운/툴팁에서). 홈 아바타 패턴 재사용.
          // 돋보기 아이콘(20px)과 정렬을 맞추려 h-5 w-5로 동일 크기.
          leadingIcon: (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-[var(--color-primary-soft-bg)] text-[10px] font-bold leading-none text-[var(--color-primary)]">
              {(project.name.charAt(0) || '?').toUpperCase()}
            </span>
          ),
        })),
      ),
    [meProjects],
  );
  const selectedScopeLabel = useMemo(
    () => projectOptions.find((option) => option.value === searchProjectId)?.label ?? '',
    [projectOptions, searchProjectId],
  );

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setDebouncedQuery('');
      setSelectedTypes([]);
      setActiveIndex(-1);
      setNavigatingId(null);
      setCurrentLimit(SEARCH_LIMIT_STEP);
      setSearchProjectId(projectId);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, projectId]);

  // Reset limit when query/filters change so new searches start at the base step
  useEffect(() => {
    setCurrentLimit(SEARCH_LIMIT_STEP);
  }, [debouncedQuery, selectedTypes]);

  const { data: metaData } = useSearchMeta(token, searchProjectId, isOpen);
  const { data, isLoading, isFetching } = useSearch(token, searchProjectId, {
    q: debouncedQuery,
    types: selectedTypes.length > 0 ? selectedTypes : undefined,
    limit: currentLimit,
  });

  const isPaginating = currentLimit > SEARCH_LIMIT_STEP;
  const isFetchingNextPage = isFetching && isPaginating;
  const showLoading = (isLoading || isFetching) && !isPaginating;
  const hasQuery = debouncedQuery.length > 0;
  const results = useMemo(() => data?.data ?? [], [data?.data]);
  const partialFailures = data?.partialFailures ?? [];
  const total = data?.meta?.total ?? 0;
  const availableProviders = data?.meta?.availableProviders ?? metaData?.meta?.availableProviders;
  const isSemanticSearchEnabled = metaData?.meta.semanticEnabled === true;
  const visibleEntityTypes = availableProviders
    ? allEntityTypes.filter((entry) => !entry.provider || availableProviders.includes(entry.provider))
    : allEntityTypes;
  const partialFailureProviders = partialFailures.map((provider) => {
    if (provider === 'github') return t('searchProviderGithub');
    if (provider === 'gitlab') return t('searchProviderGitlab');
    if (provider === 'bitbucket') return t('searchProviderBitbucket');
    if (provider === 'linear') return t('searchProviderLinear');
    if (provider === 'sentry') return t('searchProviderSentry');
    return provider;
  });

  const handleResultClick = useCallback(
    (item: (typeof results)[number]) => {
      if (navigatingId) {
        return;
      }

      if (
        item.entityType === 'GITHUB_ISSUE' ||
        item.entityType === 'GITHUB_PR' ||
        item.entityType === 'GITLAB_ISSUE' ||
        item.entityType === 'GITLAB_MERGE_REQUEST' ||
        item.entityType === 'BITBUCKET_ISSUE' ||
        item.entityType === 'BITBUCKET_PR' ||
        item.entityType === 'LINEAR_ISSUE'
      ) {
        if (item.htmlUrl) {
          window.open(item.htmlUrl, '_blank', 'noopener,noreferrer');
        }
        onClose();
        return;
      }

      const entityType = item.entityType;
      const id = entityType === 'COMMENT' ? (item.targetId ?? item.id) : item.id;
      const basePath = entityTypeRoute[entityType];
      if (onInternalResultSelect?.({ item, projectId: searchProjectId })) {
        onClose();
        return;
      }
      setNavigatingId(item.id);
      startTransition(() => {
        router.push(resolveAppHref(`${basePath}?id=${id}${currentContextQuery.replace('?', '&')}`));
      });
      setTimeout(() => onClose(), 100);
    },
    [router, currentContextQuery, onClose, navigatingId, onInternalResultSelect, searchProjectId],
  );

  const handleTypeToggle = (type: SearchEntityType) => {
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
    setActiveIndex(-1);
  };

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(results.length > 0 ? 0 : -1);
  }, [results]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (results.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev < results.length - 1 ? prev + 1 : 0;
          listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev > 0 ? prev - 1 : results.length - 1;
          listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const item = results[activeIndex];
        handleResultClick(item);
      }
    },
    [results, activeIndex, handleResultClick, onClose],
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="bottomSheet"
      size="4xl"
      height="h-[70vh] max-h-[70vh]"
      className="!p-0 flex flex-col overflow-hidden"
    >
      <div className="flex h-full flex-col" onKeyDown={handleKeyDown}>
        {/* Search Input */}
        <div className="flex items-center gap-1.5 px-4 py-3">
          {showLoading ? (
            <Spinner size="sm" className="shrink-0 text-primary" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
              className="h-5 w-5 shrink-0 text-[var(--color-fg-subtle)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
          {/* 스코프 칩: 검색어 앞(GitHub식). 이니셜 아바타 한 칸으로 공간 최소화, 풀네임은 드롭다운/툴팁. */}
          {!hideProjectSelector && projectOptions.length > 0 ? (
            <>
              <span className="inline-flex shrink-0 items-center" title={selectedScopeLabel}>
                <Select
                  size="sm"
                  value={searchProjectId}
                  onChange={setSearchProjectId}
                  options={projectOptions}
                  variant="unstyled"
                  fitContent
                  hideSelectedLabel
                  hideChevron
                  optionsAlign="start"
                  buttonClassName="inline-flex items-center justify-center rounded-md p-0.5 leading-none text-base-content transition-colors hover:bg-base-content/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                  optionsClassName="!w-max !min-w-[12rem] !max-w-[18rem]"
                />
              </span>
              <span className="h-5 w-px shrink-0 bg-base-content/10" aria-hidden />
            </>
          ) : null}
          <input
            ref={inputRef}
            type="text"
            className="min-w-0 flex-1 border-0 bg-transparent text-sm focus:outline-none placeholder:text-[var(--color-fg-subtle)]"
            placeholder={t('searchPlaceholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          {isSemanticSearchEnabled ? (
            <span
              data-testid="semantic-search-status"
              role="status"
              aria-label={t('semanticSearchLabel')}
              title={t('semanticSearchDescription')}
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-1 text-[11px] font-medium text-primary sm:px-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.75"
                  d="M9.813 15.904 9 18l-.813-2.096a4.5 4.5 0 0 0-2.591-2.591L3.5 12.5l2.096-.813a4.5 4.5 0 0 0 2.591-2.591L9 7l.813 2.096a4.5 4.5 0 0 0 2.591 2.591l2.096.813-2.096.813a4.5 4.5 0 0 0-2.591 2.591ZM18.259 8.715 18 9.5l-.259-.785a3.75 3.75 0 0 0-2.456-2.456L14.5 6l.785-.259a3.75 3.75 0 0 0 2.456-2.456L18 2.5l.259.785a3.75 3.75 0 0 0 2.456 2.456L21.5 6l-.785.259a3.75 3.75 0 0 0-2.456 2.456Z"
                />
              </svg>
              <span className="hidden sm:inline">{t('semanticSearchLabel')}</span>
            </span>
          ) : null}
          <button
            type="button"
            className={`${shortcutKeyInputClassName} hidden shrink-0 cursor-pointer transition-colors hover:border-base-content/20 hover:text-base-content sm:inline-flex`}
            onClick={onClose}
          >
            esc
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-base-content/10" />

        {/* Type Filter Chips */}
        <div className="flex gap-2 overflow-x-auto px-4 py-3" style={{ scrollbarWidth: 'none' }}>
          <button
            type="button"
            className={`badge shrink-0 cursor-pointer gap-1 border-0 py-3 text-xs transition-colors ${selectedTypes.length === 0 ? 'bg-primary/15 text-primary' : 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)] hover:bg-base-content/10'}`}
            onClick={() => setSelectedTypes([])}
          >
            {t('searchTypeAll')}
          </button>
          {visibleEntityTypes.map(({ type, labelKey }) => (
            <button
              key={type}
              type="button"
              className={`badge shrink-0 cursor-pointer gap-1 border-0 py-3 text-xs transition-colors ${selectedTypes.includes(type) ? entityTypeBadge[type].color : 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)] hover:bg-base-content/10'}`}
              onClick={() => handleTypeToggle(type)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-base-content/10" />

        {/* Results Area */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!hasQuery ? (
            <div className={SEARCH_CENTER_STATE_CLASS_NAME}>
              <AgentTeamsLottieLoader
                animate={false}
                decorative
                className={cn(SEARCH_CENTER_LOGO_CLASS_NAME, 'opacity-40')}
              />
            </div>
          ) : showLoading ? (
            <div className={SEARCH_CENTER_STATE_CLASS_NAME}>
              <AgentTeamsLottieLoader label={t('searchSearching')} className={SEARCH_CENTER_LOGO_CLASS_NAME} />
            </div>
          ) : (
            <>
              {partialFailures.length > 0 ? (
                <div className="mx-4 mt-3 rounded-xl border border-warning/20 bg-warning/10 px-3 py-2 text-xs text-[var(--color-fg-muted)]">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 shrink-0 text-warning"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.75"
                        d="M12 9v3.75m0 3h.008v.008H12v-.008zm8.25-.75a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z"
                      />
                    </svg>
                    <span>
                      {t('searchPartialFailureMessage', {
                        providers: new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(
                          partialFailureProviders,
                        ),
                      })}
                    </span>
                  </div>
                </div>
              ) : null}
              {results.length === 0 ? (
                <div className={cn(SEARCH_CENTER_STATE_CLASS_NAME, 'flex-col gap-2 text-[var(--color-fg-muted)]')}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm">{t('searchNoResults')}</span>
                </div>
              ) : (
                <>
                  <div className="px-4 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
                    {t('searchResultCount', { count: total })}
                  </div>
                  <ul ref={listRef} className="px-2 pb-2">
                    {results.map((item, index) => {
                      const statusBadge = getStatusBadge(item.entityType, item.status);
                      const providerBadge = getEntityTypeBadgeLabel(item.entityType, t);

                      return (
                        <li key={`${item.entityType}-${item.id}`}>
                          <button
                            type="button"
                            className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                              navigatingId && navigatingId !== item.id ? 'opacity-50' : ''
                            } ${
                              index === activeIndex || navigatingId === item.id
                                ? 'bg-primary/10 text-base-content'
                                : 'text-[var(--color-fg-muted)] hover:bg-base-content/5'
                            }`}
                            onClick={() => {
                              if (navigatingId && navigatingId !== item.id) {
                                return;
                              }
                              handleResultClick(item);
                            }}
                            onMouseEnter={() => setActiveIndex(index)}
                          >
                            {/* Entity Type Icon */}
                            <div
                              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${entityTypeBadge[item.entityType].color}`}
                            >
                              {item.entityType === 'GITHUB_ISSUE' || item.entityType === 'GITHUB_PR' ? (
                                <GitHubIcon className="h-3.5 w-3.5" />
                              ) : item.entityType === 'GITLAB_ISSUE' || item.entityType === 'GITLAB_MERGE_REQUEST' ? (
                                <GitLabIcon className="h-3.5 w-3.5" />
                              ) : item.entityType === 'BITBUCKET_ISSUE' || item.entityType === 'BITBUCKET_PR' ? (
                                <BitbucketIcon className="h-3.5 w-3.5" />
                              ) : item.entityType === 'LINEAR_ISSUE' ? (
                                <LinearIcon className="h-3.5 w-3.5" />
                              ) : item.entityType === 'SENTRY_ISSUE' ? (
                                <SentryIcon className="h-3.5 w-3.5" />
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d={entityTypeIcon[item.entityType]}
                                  />
                                </svg>
                              )}
                            </div>

                            {/* Content */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-medium text-base-content">
                                  {item.entityType === 'COMMENT' && item.snippet
                                    ? sanitizeSnippet(item.snippet)
                                    : item.title}
                                </span>
                                {providerBadge ? (
                                  <span className={`${badgeSizes.xxs} shrink-0 ${providerBadge.className}`}>
                                    {providerBadge.label}
                                  </span>
                                ) : null}
                                {ICON_STATUS_ENTITY_TYPES.has(item.entityType) && item.status ? (
                                  <EntityStatusBadge
                                    entityType={item.entityType as StatusBadgeEntityType}
                                    status={item.status}
                                    size="xxs"
                                    className="shrink-0"
                                  />
                                ) : statusBadge ? (
                                  <span className={`${badgeSizes.xxs} shrink-0 ${statusBadge.tone}`}>
                                    {statusBadge.label}
                                  </span>
                                ) : null}
                                {item.archived ? (
                                  <span
                                    className={`${badgeSizes.xxs} shrink-0 bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]`}
                                  >
                                    {t('searchArchivedBadge')}
                                  </span>
                                ) : null}
                              </div>
                              {item.entityType === 'COMMENT' ? (
                                <span className="mt-0.5 line-clamp-1 text-xs text-[var(--color-fg-muted)]">
                                  Plan: {item.title}
                                </span>
                              ) : item.snippet ? (
                                <span className="mt-0.5 line-clamp-1 text-xs text-[var(--color-fg-muted)]">
                                  {sanitizeSnippet(item.snippet)}
                                </span>
                              ) : null}
                            </div>

                            {/* Date */}
                            {navigatingId === item.id ? (
                              <Spinner className="text-primary shrink-0" />
                            ) : (
                              <span className="mt-0.5 shrink-0 text-[11px] text-[var(--color-fg-subtle)]">
                                {formatRelativeDate(item.createdAt)}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <LoadMoreButton
                    hasNextPage={
                      isFetchingNextPage || (results.length >= currentLimit && currentLimit < SEARCH_LIMIT_MAX)
                    }
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={() =>
                      setCurrentLimit((prev) => Math.min(prev + SEARCH_LIMIT_STEP, SEARCH_LIMIT_MAX))
                    }
                    label={tCommon('actions.loadMore')}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* Footer - Keyboard Hints */}
        <div className="hidden items-center gap-4 border-t border-base-content/10 px-4 py-2 text-[11px] text-[var(--color-fg-subtle)] sm:flex">
          <span className="flex items-center gap-1">
            <span className={shortcutKeyClassName}>↑</span>
            <span className={shortcutKeyClassName}>↓</span>
            {t('searchHintNavigate')}
          </span>
          <span className="flex items-center gap-1">
            <span className={shortcutKeyClassName}>↵</span>
            {t('searchHintSelect')}
          </span>
          <span className="flex items-center gap-1">
            <span className={shortcutKeyClassName}>esc</span>
            {t('searchHintClose')}
          </span>
        </div>
      </div>
    </BaseModal>
  );
};
